import React, { useReducer, useEffect, useCallback } from 'react';
import { GraphQLClient } from 'graphql-request';
import { useQueryClient } from 'react-query';
import { useHistory } from 'react-router-dom';

import FullPageLoading from 'components/full-page-loading';
import { meQuery } from 'hooks/auth/queries';
import {
  LOGIN_MUTATION,
  RESET_PASSWORD_REQUEST_MUTATION,
  RESET_PASSWORD_MUTATION,
  CONFIRM_EMAIL_MUTATION,
  resendConfirmEmailMutation,
} from 'hooks/auth/mutations';
import { baseURL } from 'utils/api';

const AuthContext = React.createContext();
const storageKey = 'nnn-auth';

const reducer = (state, action) => {
  switch (action.type) {
    case 'FETCH_USER':
      return { ...state, user: null, loading: true };
    case 'SET_USER':
      return { ...state, user: action.payload, loading: false };
    case 'REMOVE_USER':
      return { ...state, user: null, loading: false };
    default:
      throw new Error('Action not supported');
  }
};

const initialState = {
  user: null,
  loading: true,
};

function AuthProvider(props) {
  const queryCache = useQueryClient();
  const client = new GraphQLClient(baseURL, {
    mode: 'cors',
  });
  const history = useHistory();
  const [state, dispatch] = useReducer(reducer, initialState);

  const validateToken = useCallback(() => {
    let storedUser = localStorage.getItem(storageKey);

    if (storedUser === null) {
      dispatch({ type: 'SET_USER', payload: { user: null } });
      return null;
    }

    try {
      const parsed = JSON.parse(storedUser);
      if (parsed.token == null) {
        throw new Error('No token key');
      }
    } catch (e) {
      storedUser = null;
      localStorage.removeItem(storageKey);
      console.warn('Error parsing user token');
      console.error(e);
    }

    const user = storedUser ? JSON.parse(storedUser) : null;

    if (user == null) {
      return dispatch({ type: 'SET_USER', payload: null });
    }

    dispatch({ type: 'FETCH_USER' });

    return client
      .request(
        meQuery,
        {},
        {
          Authorization: `Bearer ${user.token}`,
        }
      )
      .then(() => {
        dispatch({ type: 'SET_USER', payload: user });
      })
      .catch(() => {
        console.info('User token invalid, removing ...');
        dispatch({ type: 'REMOVE_USER' });
        localStorage.removeItem(storageKey);
        history.push('/');
      });
  }, [history]);

  useEffect(() => {
    validateToken();
  }, [validateToken]);

  if (state.loading) {
    return <FullPageLoading header={false} delay={1000} />;
  }

  function handleUser(res) {
    const {
      login: { token },
    } = res;

    localStorage.setItem(storageKey, JSON.stringify({ token }));
    dispatch({ type: 'SET_USER', payload: { token } });
  }

  function login(values) {
    return client
      .request(LOGIN_MUTATION, values)
      .then(handleUser)
      .catch((error) => Promise.reject(error));
  }

  function resetPasswordRequest(values) {
    return client.request(RESET_PASSWORD_REQUEST_MUTATION, values);
  }

  function resetPassword(values) {
    return client.request(RESET_PASSWORD_MUTATION, values);
  }

  // function resetPasswordValidate(values) {
  //   return client.request(
  //     ,
  //     values
  //   );
  // }

  function confirmEmail(values) {
    return client.request(CONFIRM_EMAIL_MUTATION, values);
  }

  function resendConfirmEmail(values) {
    return client.request(resendConfirmEmailMutation, values);
  }

  // function register(values) {
  //   return client
  //     .request(, values)
  //     .catch((error) => Promise.reject(error));
  // }

  function logout() {
    dispatch({ type: 'REMOVE_USER' });
    queryCache.clear();
    history.push('/');
    localStorage.removeItem(storageKey);
  }

  return (
    <AuthContext.Provider
      value={{
        user: state.user,
        login,
        logout,
        // register,
        validateToken,
        resetPassword,
        resetPasswordRequest,
        confirmEmail,
        resendConfirmEmail,
      }}
      {...props}
    />
  );
}

const useAuth = () => React.useContext(AuthContext);

export { AuthProvider, useAuth };
